import React, { useState } from "react";
import { PlusOneEntry } from "../types";
import { formatPrice } from "../utils/format";

const ALLOTMENT_EXHAUSTED =
  "You've used all of your plus-one seats. No more plus-ones can be added.";

interface Props {
  limit: number;
  ticketPrice: number;
  loading?: boolean;
  error?: string;
  onSubmit: (plusOnes: PlusOneEntry[]) => void;
  onSkip?: () => void;
  showSkip?: boolean;
  skipLabel?: string;
  allowSelfPay?: boolean;
  onSelfPay?: (plusOnes: PlusOneEntry[]) => void;
}

const emptyEntry = (): PlusOneEntry => ({ full_name: "", email: "" });

const isCompleteEntry = (entry: PlusOneEntry) =>
  entry.full_name.trim().length > 0 && (entry.email?.trim().length ?? 0) > 0;

const PlusOneForm: React.FC<Props> = ({
  limit,
  ticketPrice,
  loading,
  error,
  onSubmit,
  onSkip,
  showSkip = true,
  skipLabel = "Skip Plus-Ones",
  allowSelfPay = false,
  onSelfPay,
}) => {
  const [plusOnes, setPlusOnes] = useState<PlusOneEntry[]>([emptyEntry()]);
  const [validationError, setValidationError] = useState("");

  const handleChange = (index: number, field: keyof PlusOneEntry, value: string) => {
    setPlusOnes((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry))
    );
    setValidationError("");
  };

  const addSlot = () => {
    if (plusOnes.length >= limit) {
      setValidationError(ALLOTMENT_EXHAUSTED);
      return;
    }
    setPlusOnes((prev) => [...prev, emptyEntry()]);
    setValidationError("");
  };

  const completeEntries = () =>
    plusOnes.filter(isCompleteEntry).map((entry) => ({
      full_name: entry.full_name.trim(),
      email: entry.email?.trim() || "",
    }));

  const collectComplete = () => {
    const hasPartial = plusOnes.some((entry) => {
      const hasName = entry.full_name.trim().length > 0;
      const hasEmail = (entry.email?.trim().length ?? 0) > 0;
      return (hasName && !hasEmail) || (!hasName && hasEmail);
    });

    if (hasPartial) {
      setValidationError("Each plus-one must include both a full name and email.");
      return null;
    }

    const complete = completeEntries();
    if (complete.length === 0) {
      setValidationError(
        showSkip
          ? "Enter at least one plus-one with a full name and email, or skip."
          : "Enter at least one plus-one with a full name and email."
      );
      return null;
    }

    if (complete.length > limit) {
      setValidationError(ALLOTMENT_EXHAUSTED);
      return null;
    }

    return complete;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const complete = collectComplete();
    if (!complete) return;
    onSubmit(complete);
  };

  const handleSelfPay = () => {
    const complete = collectComplete();
    if (!complete) return;
    onSelfPay?.(complete);
  };

  const completeCount = plusOnes.filter(isCompleteEntry).length;
  const totalCost = completeCount * ticketPrice;
  const displayError = validationError || error;
  const paid = ticketPrice > 0;
  const submitLabel = paid ? "Pay for Plus-Ones" : "Add Plus-Ones";
  const atAllotment = plusOnes.length >= limit;

  if (limit <= 0) {
    return (
      <div className="form">
        <h3>Add Your Plus-Ones</h3>
        <div className="error-message inline">{ALLOTMENT_EXHAUSTED}</div>
      </div>
    );
  }

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>
      <h3>Add Your Plus-Ones</h3>
      <p className="form-hint">
        You may add up to {limit} more guest{limit !== 1 ? "s" : ""}. Each plus-one
        requires a full name and email.
        {paid ? ` Each ticket costs ${formatPrice(ticketPrice)}.` : " These seats are complimentary."}
        {allowSelfPay && paid
          ? " If you do not pay now, we will email each plus-one a payment link. You can also come back later to pay or add more guests."
          : ""}
      </p>

      {displayError && <div className="error-message inline">{displayError}</div>}

      {plusOnes.map((entry, index) => (
        <div key={index} className="plus-one-row">
          <h4>Plus-One {index + 1}</h4>
          <div className="form-group">
            <label htmlFor={`plus_name_${index}`}>Full Name *</label>
            <input
              id={`plus_name_${index}`}
              value={entry.full_name}
              onChange={(e) => handleChange(index, "full_name", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor={`plus_email_${index}`}>Email *</label>
            <input
              id={`plus_email_${index}`}
              type="email"
              value={entry.email || ""}
              onChange={(e) => handleChange(index, "email", e.target.value)}
            />
          </div>
        </div>
      ))}

      {plusOnes.length < limit && (
        <div className="button-row" style={{ marginBottom: "1rem" }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={addSlot}>
            Add another plus-one
          </button>
        </div>
      )}
      {atAllotment && limit > 1 && (
        <p className="form-hint">{ALLOTMENT_EXHAUSTED}</p>
      )}

      {completeCount > 0 && paid && (
        <p className="form-hint">
          Total: {formatPrice(totalCost)} for {completeCount} plus-one
          {completeCount !== 1 ? "s" : ""}
        </p>
      )}

      <div className="button-row">
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? "Processing..." : submitLabel}
        </button>
        {allowSelfPay && paid && onSelfPay && (
          <button
            type="button"
            className="btn btn-secondary"
            disabled={loading}
            onClick={handleSelfPay}
          >
            Email them a payment link
          </button>
        )}
        {showSkip && onSkip && (
          <button
            type="button"
            className="btn btn-secondary"
            disabled={loading}
            onClick={onSkip}
          >
            {skipLabel}
          </button>
        )}
      </div>
    </form>
  );
};

export default PlusOneForm;
